import { request, type Filters } from "./api.ts";

/**
 * Экран «B2B» — воронка сделок B2B (школы, компании, агентства). Сервер —
 * `server/src/api/b2b.ts`, данные — `core.v_b2b_deal` (миграция 148).
 *
 * Деньги — в AED, минорными единицами (филсы): у B2B одна валюта, Бахрейна в
 * воронке нет. Даты — строкой `YYYY-MM-DD`, время мероприятия — `HH:MM` по парку.
 */

/** Числа приходят из Postgres: count — числом, суммы bigint — строкой. */
type Num = number | string;

export type B2bTotals = {
  requests: number;
  no_park: number;
  invoice_sent: number;
  invoice_paid: number;
  won: number;
  lost: number;
  open: number;
  replied: number;
  /** Медиана первого ответа GM, секунды; null — ответов в периоде нет. */
  reply_median_s: number | null;
  /** Открытые сделки с перепиской, где GM ещё не ответил. */
  awaiting_reply: number;
  /** Передано GM, но переписки открытой линии у сделки нет — ответ измерить нечем. */
  no_dialog: number;
  escalated: number;
  won_events: number;
  revenue_aed_minor: Num;
  prepayments: number;
  prepaid_aed_minor: Num;
  with_balance: number;
  balance_aed_minor: Num;
  /** Открытые сделки дальше Invoice Sent без суммы сделки — сейчас, без периода. */
  no_amount: number;
};

export type B2bStage = {
  stage_id: string;
  name: string;
  role: string | null;
  semantics: "S" | "F" | null;
  sort: number;
  /** Сколько сделок периода когда-либо дошли до стадии. */
  reached: number;
  /** Сколько стоит на ней сейчас. */
  current: number;
};

export type B2bParkRow = {
  park_key: string | null;
  park_name: string;
  requests: number;
  invoice_paid: number;
  won: number;
  lost: number;
  won_aed_minor: Num;
  reply_median_s: number | null;
  escalated: number;
};

export type B2bChannelRow = { channel: string; requests: number; invoice_paid: number; won: number };

export type B2bEvent = {
  deal_id: number | string;
  title: string | null;
  organization: string | null;
  park_key: string | null;
  park_name: string | null;
  date_of_event: string;
  starts_at: string | null;
  ends_at: string | null;
  rooms: string | null;
  kids: number | null;
  adults: number | null;
  event_type: string | null;
  client_type: string | null;
  stage_name: string | null;
  stage_role: string | null;
  manager_name: string | null;
  opportunity_aed_minor: Num | null;
  prepaid_aed_minor: Num | null;
  balance_aed_minor: Num | null;
  bitrix_url: string;
};

export type B2bDeal = {
  deal_id: number | string;
  title: string | null;
  organization: string | null;
  park_key: string | null;
  park_name: string | null;
  source_name: string | null;
  stage_name: string | null;
  stage_role: string | null;
  created_day: string;
  date_of_event: string | null;
  client_type: string | null;
  event_type: string | null;
  manager_name: string | null;
  opportunity_aed_minor: Num | null;
  prepaid_aed_minor: Num | null;
  balance_aed_minor: Num | null;
  gm_reply_seconds: number | null;
  escalated: boolean;
  suvvy_handoff_code: string | null;
  loss_reason: string | null;
  bitrix_url: string;
};

export type B2bReport = {
  filters: { from: string; to: string };
  totals: B2bTotals;
  funnel: B2bStage[];
  byPark: B2bParkRow[];
  byChannel: B2bChannelRow[];
  handoffs: Array<{ code: string; deals: number }>;
  losses: Array<{ reason: string; deals: number }>;
  upcoming: B2bEvent[];
  deals: B2bDeal[];
  queryMs: number;
};

function filterParams(f: Filters): URLSearchParams {
  const p = new URLSearchParams({ from: f.from, to: f.to });
  if (f.park?.length) p.set("park", f.park.join(","));
  if (f.brand?.length) p.set("brand", f.brand.join(","));
  return p;
}

export function fetchB2b(f: Filters): Promise<B2bReport> {
  return request<B2bReport>(`/api/dashboard/b2b?${filterParams(f).toString()}`);
}

// ------------------------------------------------------------ список под числом

/**
 * Величина экрана — ключ из `METRIC` в server/src/api/b2b.ts. Условие числа и
 * условие списка под ним — одна запись на сервере.
 */
export type B2bMetric =
  | "requests" | "no_park" | "invoice_sent" | "invoice_paid" | "won" | "lost" | "open"
  | "replied" | "awaiting_reply" | "no_dialog" | "escalated"
  | "won_events" | "prepayments" | "with_balance" | "no_amount" | "upcoming";

/**
 * Срез строки таблицы поверх величины. Пустая строка у канала, кода передачи и
 * причины — «не указано» (так строка и посчитана); парк `none` — «парк не указан».
 */
export type B2bSlice = {
  metric: B2bMetric;
  park?: string;
  channel?: string;
  /** Дошли до стадии — строка воронки. */
  reached?: string;
  /** Стоят на стадии сейчас. */
  stage?: string;
  handoff?: string;
  loss?: string;
};

/** Детализация под числом экрана B2B — вид `b2b` общего `Drill` (dealList.tsx). */
export type B2bDrill = {
  title: string;
  /** По какому условию собран список — пишется под заголовком панели. */
  hint?: string;
  slice: B2bSlice;
};

export type B2bListRow = {
  deal_id: number | string;
  title: string | null;
  organization: string | null;
  park_key: string | null;
  park_name: string | null;
  source_name: string | null;
  stage_id: string;
  stage_name: string | null;
  stage_role: string | null;
  created_day: string;
  /** Создана, по Дубаю: «2026-10-30 12:36». */
  created_local: string;
  date_of_event: string | null;
  client_type: string | null;
  event_type: string | null;
  rooms: string | null;
  kids: number | null;
  adults: number | null;
  manager_name: string | null;
  opportunity_aed_minor: Num | null;
  prepaid_aed_minor: Num | null;
  prepaid_on: string | null;
  /** Откуда предоплата: бронь B2B или поле CRM «Prepayment confirmed». */
  prepaid_source: "booking" | "crm" | null;
  balance_aed_minor: Num | null;
  gm_in_work_local: string | null;
  gm_reply_local: string | null;
  gm_reply_seconds: number | null;
  ol_sessions: number | null;
  escalated: boolean;
  suvvy_handoff_code: string | null;
  loss_reason: string | null;
  bitrix_url: string;
};

export type B2bListPage = {
  total: number;
  amount_aed_minor: Num;
  prepaid_aed_minor: Num;
  balance_aed_minor: Num;
  rows: B2bListRow[];
};

export function fetchB2bDeals(f: Filters, slice: B2bSlice, limit = 200, offset = 0): Promise<B2bListPage> {
  const p = filterParams(f);
  p.set("metric", slice.metric);
  /* Парк среза — не фильтр панели: на сервере это отдельный параметр, и он
     сужает уже отфильтрованное панелью. */
  if (slice.park !== undefined) p.set("slicePark", slice.park);
  if (slice.channel !== undefined) p.set("channel", slice.channel);
  if (slice.reached !== undefined) p.set("reached", slice.reached);
  if (slice.stage !== undefined) p.set("stage", slice.stage);
  if (slice.handoff !== undefined) p.set("handoff", slice.handoff);
  if (slice.loss !== undefined) p.set("loss", slice.loss);
  p.set("limit", String(limit));
  p.set("offset", String(offset));
  return request<B2bListPage>(`/api/b2b/deals?${p.toString()}`);
}
